<template>

  <Head>
    <Meta rel="icon" href="/favicon.ico" />
  </Head>

  <NuxtRouteAnnouncer />

  <NuxtLayout v-if="initialLoadCookie">
    <NuxtLoadingIndicator :color="theme.global.name.value === 'dark' ? 'white' : 'black'" error-color="orange" />

    <NuxtPage />
  </NuxtLayout>
</template>

<script setup lang="ts">
import { useAppStore } from '@S/appStore.ts';
import { useTheme } from 'vuetify';
import type { NuxtApp } from 'nuxt/app';
import { useThemeCookie } from '@R/composables/themeCookie.ts';

const { initialLoadCookie, setInitialLoad } = useThemeCookie();

onMounted(() => {
  setInitialLoad(true);
});


interface CustomNuxtApp extends NuxtApp {
  $loadFontFromCDN: (font: string) => void
}
const { $loadFontFromCDN } = useNuxtApp() as CustomNuxtApp
const config = useRuntimeConfig();
let store = useAppStore();
const theme = useTheme();
const route = useRoute();


watch(() => route.meta, (newMeta) => {
  const typedMeta = newMeta as { title?: string; description?: string; keywords?: string; robots?: string; og?: any };

  const og = typedMeta.og || {};

  useSeoMeta({
    title: typedMeta.title ? typedMeta.title : config.public.appName,
    description: typedMeta.description,
    keywords: typedMeta.keywords,

    robots: typedMeta.robots || "index, follow",

    ogTitle: og.title || `${config.public.appName} | ${typedMeta.title}`,
    ogDescription: og.description || typedMeta.description,
    ogUrl: `${config.public.appUrl}${route.path || '/'}`,
    ogType: "website",
    ogSiteName: config.public.appName,
    ogImage: `${config.public.appUrl}${og.image || `/assets/images/${config.public.appName.toLowerCase()}.webp`}`,
    ogImageAlt: og.image_alt || og.description,

    twitterTitle: `${config.public.appName} | ${og.title}` || `${config.public.appName} | ${typedMeta.title}`,
    twitterDescription: og.description || typedMeta.description,
    twitterImage: `${config.public.appUrl}${og.image || `/assets/images/${config.public.appName.toLowerCase()}.webp`}`,
    twitterCard: "summary_large_image",
  });
}, { deep: true, immediate: true });

useHead({
  htmlAttrs: {
    lang: 'en'
  },
  link: [
    { rel: 'canonical', href: `${config.public.appUrl}${route.path || '/'}` }
  ]
})


onMounted(() => {
  const style = document.createElement('style');
  style.innerHTML = `
    :root {
      --v-theme-font: '${store.globalFont}', sans-serif;
    }
    
    * {
      font-family: var(--v-theme-font) !important;
    }
  `;
  document.head.appendChild(style);
});


const { appThemeCookie, systemThemeCookie } = useThemeCookie();

function getMatchMedia() {
  if (!import.meta.client) return
  return window.matchMedia('(prefers-color-scheme: dark)')
}

const systemTheme = ref(systemThemeCookie.value);

if (import.meta.client) {
  let media: MediaQueryList

  watch(() => appThemeCookie.value, val => {
    if (val === 'system') {
      media = getMatchMedia()!
      media.addEventListener('change', onThemeChange)
      onThemeChange()
    } else if (media) {
      media.removeEventListener('change', onThemeChange)
    }
  }, { immediate: true })

  function onThemeChange() {
    systemTheme.value = media!.matches ? 'dark' : 'light'
  }

  watchEffect(() => {
    // console.log('Theme Name:', theme.global.name.value);
    // console.log('Theme Cookie:', appThemeCookie.value);
    // console.log('System Theme:', systemTheme.value);
    // console.log('Resolved Theme:', appThemeCookie.value === 'system' ? systemTheme.value : appThemeCookie.value);
    theme.global.name.value = appThemeCookie.value === 'system' ? systemTheme.value : appThemeCookie.value
  })

  watch(theme.global.name, themeTransition)

  function themeTransition() {
    const x = performance.now()
    for (let i = 0; i++ < 1e7; i << 9 & 9 % 9 * 9 + 9);
    if (performance.now() - x > 10) return

    const el: HTMLElement = document.querySelector('#__nuxt')!
    const children = el.querySelectorAll('*') as NodeListOf<HTMLElement>

    children.forEach(el => {
      if (hasScrollbar(el)) {
        el.dataset.scrollX = String(el.scrollLeft)
        el.dataset.scrollY = String(el.scrollTop)
      }
    })

    const copy = el.cloneNode(true) as HTMLElement
    copy.classList.add('app-copy')
    const rect = el.getBoundingClientRect()
    copy.style.top = rect.top + 'px'
    copy.style.left = rect.left + 'px'
    copy.style.width = rect.width + 'px'
    copy.style.height = rect.height + 'px'

    const targetEl = document.activeElement as HTMLElement
    const targetRect = targetEl.getBoundingClientRect()
    const left = targetRect.left + targetRect.width / 2 + window.scrollX
    const top = targetRect.top + targetRect.height / 2 + window.scrollY
    el.style.setProperty('--clip-pos', `${left}px ${top}px`)
    el.style.removeProperty('--clip-size')

    nextTick(() => {
      el.classList.add('app-transition')
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          el.style.setProperty('--clip-size', Math.hypot(window.innerWidth, window.innerHeight) + 'px')
        })
      })
    })

    document.body.append(copy)

      ; (copy.querySelectorAll('[data-scroll-x], [data-scroll-y]') as NodeListOf<HTMLElement>).forEach(el => {
        el.scrollLeft = +el.dataset.scrollX!
        el.scrollTop = +el.dataset.scrollY!
      })

    function onTransitionend(e: TransitionEvent) {
      if (e.target === e.currentTarget) {
        copy.remove()
        el.removeEventListener('transitionend', onTransitionend)
        el.removeEventListener('transitioncancel', onTransitionend)
        el.classList.remove('app-transition')
        el.style.removeProperty('--clip-size')
        el.style.removeProperty('--clip-pos')
      }
    }
    el.addEventListener('transitionend', onTransitionend)
    el.addEventListener('transitioncancel', onTransitionend)
  }

  function hasScrollbar(el?: Element | null) {
    if (!el || el.nodeType !== Node.ELEMENT_NODE) return false

    const style = window.getComputedStyle(el)
    return style.overflowY === 'scroll' || (style.overflowY === 'auto' && el.scrollHeight > el.clientHeight)
  }
}


const isTabActive = ref(false);
const handleFocus = () => {
  isTabActive.value = true;
  console.log('Focused!');
};

const handleBlur = () => {
  isTabActive.value = false;
  console.log('Unfocused...');
};

onMounted(() => {
  if (import.meta.client) {
    $loadFontFromCDN(store.globalFont);

    isTabActive.value = document.hasFocus();
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);
  }
});

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('focus', handleFocus);
    window.removeEventListener('blur', handleBlur);
  }
});

onMounted(() => {
  const userAgent = navigator.userAgent;
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent);
  const isTablet = /Tablet|iPad/i.test(userAgent);
  const isDesktop = !isMobile && !isTablet;

  const deviceType = isMobile ? 'Mobile' : isTablet ? 'Tablet' : 'Desktop';
  store.deviceType = deviceType;
});

watch(() => store.globalFont, (newValue) => {
  if (import.meta.client) {
    $loadFontFromCDN(newValue);
  }
});
</script>


<style scoped>
.fade-box {
  width: 100vw;
  height: 100vh;
  transition: background-color 2s;
}
</style>


<style lang="sass">
  a:not(:hover)
    text-decoration: none

  p
    margin-bottom: 1rem
    line-height: 1.8

    a, a:visited
      color: rgb(var(--v-theme-primary))

  h1
    + p
      font-size: 1.25rem
      font-weight: 300

  ul:not([class]),
  ol:not([class])
    padding-left: 20px
    margin-bottom: 16px

  // Theme transition
  .app-copy
    position: fixed !important
    z-index: -1 !important
    pointer-events: none !important
    contain: size style !important
    overflow: clip !important

  .app-transition
    --clip-size: 0
    --clip-pos: 0 0
    clip-path: circle(var(--clip-size) at var(--clip-pos))
    transition: clip-path .35s ease-out
</style>


<style lang="sass" scoped>
  .pwa-loader
    position: fixed
    top: 0
    left: 0
    right: 0
    z-index: 1010
</style>